import { useState } from 'react';

export default function LandingPage() {
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [companySlug, setCompanySlug] = useState('');

    const handleGoToLogin = () => {
        if (companySlug.trim()) {
            window.location.href = `/${companySlug.trim().toLowerCase()}/login`;
        }
    };

    const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
        e.preventDefault();
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <div className="min-h-screen bg-black relative overflow-hidden">
            {/* Animated Star Field */}
            <div className="fixed inset-0 z-0">
                {[...Array(100)].map((_, i) => (
                    <div
                        key={i}
                        className="absolute bg-white rounded-full"
                        style={{
                            width: `${Math.random() * 3 + 1}px`,
                            height: `${Math.random() * 3 + 1}px`,
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                            opacity: Math.random() * 0.8 + 0.2,
                            animation: `twinkle ${Math.random() * 3 + 2}s ease-in-out infinite`,
                            animationDelay: `${Math.random() * 3}s`,
                        }}
                    />
                ))}
            </div>

            {/* Nebula gradients */}
            <div className="fixed inset-0 z-0 opacity-30">
                <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-600 rounded-full filter blur-[150px]" />
                <div className="absolute bottom-1/4 right-1/4 w-72 h-72 bg-blue-600 rounded-full filter blur-[120px]" />
                <div className="absolute top-1/2 right-1/3 w-64 h-64 bg-pink-600 rounded-full filter blur-[100px]" />
            </div>

            {/* CSS for animations */}
            <style>{`
                @keyframes twinkle {
                    0%, 100% { opacity: 0.2; }
                    50% { opacity: 1; }
                }
            `}</style>

            {/* Login Modal */}
            {showLoginModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-md">
                    <div className="bg-gray-900 rounded-2xl p-8 max-w-md w-full mx-4 border border-gray-700/50 shadow-2xl">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-bold text-white">Find your company</h3>
                            <button
                                onClick={() => setShowLoginModal(false)}
                                className="text-gray-400 hover:text-white text-2xl"
                            >
                                ×
                            </button>
                        </div>
                        <p className="text-gray-400 mb-6">
                            Enter your company's unique identifier to access your workspace.
                        </p>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Company Slug
                            </label>
                            <div className="flex items-center">
                                <span className="text-gray-500 mr-2">stellar.app/</span>
                                <input
                                    type="text"
                                    value={companySlug}
                                    onChange={(e) => setCompanySlug(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleGoToLogin()}
                                    placeholder="your-company"
                                    className="flex-1 px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    autoFocus
                                />
                            </div>
                            <p className="text-gray-500 text-sm mt-2">
                                Example: demo, acme, mycompany
                            </p>
                        </div>
                        <button
                            onClick={handleGoToLogin}
                            disabled={!companySlug.trim()}
                            className="w-full py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition"
                        >
                            Continue to Login
                        </button>
                        <p className="text-gray-500 text-sm mt-4 text-center">
                            Don't know your company slug? Ask your administrator.
                        </p>
                    </div>
                </div>
            )}

            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-black/50 backdrop-blur-md border-b border-white/10">
                <div className="max-w-7xl mx-auto px-6 py-4">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                            <ShootingStarLogo className="w-8 h-8" />
                            <span className="text-2xl font-bold text-white">Stellar</span>
                        </div>
                        <div className="flex items-center gap-6">
                            <a href="#features" onClick={(e) => scrollToSection(e, 'features')} className="text-gray-300 hover:text-white transition">Features</a>
                            <button
                                onClick={() => setShowLoginModal(true)}
                                className="text-gray-300 hover:text-white transition"
                            >
                                Login
                            </button>
                            <button
                                onClick={() => setShowLoginModal(true)}
                                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition"
                            >
                                Start Free Trial
                            </button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="relative z-10 pt-32 pb-20 px-6">
                <div className="max-w-5xl mx-auto text-center">
                    <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
                        Gamify
                        Company
                        <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
                            {" "}Culture.{" "}
                        </span>
                    </h1>

                    <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto">
                        Bring fun to your workplace by setting up interactive games that drive company culture, and more!
                    </p>

                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <button
                            onClick={() => setShowLoginModal(true)}
                            className="px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white text-lg font-semibold rounded-xl transition shadow-lg shadow-purple-500/25"
                        >
                            Start 30-Day Free Trial
                        </button>
                        <a
                            href="#features"
                            onClick={(e) => scrollToSection(e, 'features')}
                            className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white text-lg font-semibold rounded-xl transition border border-white/20"
                        >
                            Learn More
                        </a>
                    </div>

                    <p className="text-gray-500 text-sm mt-6">
                        No credit card required · Cancel anytime
                    </p>
                </div>
            </section>

            {/* Features Section */}
            <section id="features" className="relative z-10 py-20 px-6 bg-black/30">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold text-white mb-4">Everything You Need</h2>
                        <p className="text-gray-400 text-lg">Powerful tools to engage and manage your team</p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        <FeatureCard
                            icon="🎯"
                            title="Bingo Games"
                            description="Create custom bingo cards for team building, onboarding, or just for fun. Track achievements and celebrate wins."
                        />
                        <FeatureCard
                            icon="📅"
                            title="Leave Tracker"
                            description="Smart leave management with approval workflows, balance tracking, and calendar integration."
                        />
                        <FeatureCard
                            icon="💬"
                            title="Slack & Teams"
                            description="Get real-time notifications in your favorite communication tools. Stay connected always."
                        />
                        <FeatureCard
                            icon="🎨"
                            title="Custom Branding"
                            description="Make it yours with customizable themes, logos, and color schemes that match your brand."
                        />
                        <FeatureCard
                            icon="📊"
                            title="Analytics"
                            description="Track engagement, participation rates, and team performance with insightful dashboards."
                        />
                        <FeatureCard
                            icon="🔒"
                            title="Secure & Private"
                            description="Enterprise-grade security with role-based access control and data encryption."
                        />
                    </div>
                </div>
            </section>


            {/* CTA Section */}
            <section className="relative z-10 py-20 px-6 bg-gradient-to-r from-purple-900/30 to-blue-900/30 border-y border-white/10">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-4xl font-bold text-white mb-4">
                        Ready to Transform Your Workplace?
                    </h2>
                    <p className="text-gray-400 text-lg mb-8">
                        Join hundreds of companies building better culture with Stellar
                    </p>
                    <button
                        onClick={() => setShowLoginModal(true)}
                        className="inline-block px-8 py-4 bg-white text-gray-900 text-lg font-semibold rounded-xl hover:bg-gray-100 transition"
                    >
                        Get Started Today →
                    </button>
                </div>
            </section>

            {/* Footer */}
            <footer className="relative z-10 py-12 px-6 bg-black/50 border-t border-white/10">
                <div className="max-w-6xl mx-auto">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                        <div className="flex items-center gap-2">
                            <ShootingStarLogo className="w-6 h-6" />
                            <span className="text-xl font-bold text-white">Stellar</span>
                        </div>
                        <div className="flex gap-6 text-gray-400 text-sm">
                            <a href="/privacy" className="hover:text-white transition">Privacy Policy</a>
                            <a href="/terms" className="hover:text-white transition">Terms of Service</a>
                        </div>
                        <p className="text-gray-500 text-sm">
                            © 2026 Beyond Seek Technologies. All rights reserved.
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
}

function ShootingStarLogo({ className = '' }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
                <linearGradient id="starGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#a855f7" />
                    <stop offset="50%" stopColor="#ec4899" />
                    <stop offset="100%" stopColor="#3b82f6" />
                </linearGradient>
                <linearGradient id="trailGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#a855f7" stopOpacity="0" />
                    <stop offset="100%" stopColor="#a855f7" stopOpacity="0.8" />
                </linearGradient>
            </defs>
            {/* Shooting trail */}
            <path
                d="M4 28L16 16"
                stroke="url(#trailGradient)"
                strokeWidth="2"
                strokeLinecap="round"
            />
            <path
                d="M8 24L16 16"
                stroke="url(#trailGradient)"
                strokeWidth="1.5"
                strokeLinecap="round"
                opacity="0.6"
            />
            {/* Star */}
            <path
                d="M20 4L21.8 9.4L27.5 9.4L22.9 13L24.7 18.4L20 14.8L15.3 18.4L17.1 13L12.5 9.4L18.2 9.4L20 4Z"
                fill="url(#starGradient)"
            />
            {/* Sparkles */}
            <circle cx="26" cy="6" r="1" fill="#ec4899" opacity="0.8" />
            <circle cx="28" cy="12" r="0.8" fill="#a855f7" opacity="0.6" />
        </svg>
    );
}

function FeatureCard({ icon, title, description }: { icon: string; title: string; description: string }) {
    return (
        <div className="p-6 bg-white/5 border border-white/10 rounded-2xl hover:bg-white/10 transition backdrop-blur-sm">
            <div className="text-4xl mb-4">{icon}</div>
            <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
            <p className="text-gray-400">{description}</p>
        </div>
    );
}


